import type { LmsMediaFetchers } from 'shared/src/components/LearnHouse/lmsMediaContext';
import type {
  LmsActivity,
  LmsAssignment,
  LmsAssignmentSubmitResult,
  LmsAssignmentSubmitTask,
  LmsAssignmentTask,
  LmsChapter,
  LmsCourseDetail,
  LmsCourseProgress,
  LmsQuizAttempt,
  LmsQuizQuestion,
  LmsQuizSummary,
} from 'shared/src/types/lms';
import { LmsAssignmentType, LmsEnrollmentStatus, LmsGradingType, LmsQuizStatus, parseLmsGradingType } from 'shared/src/utils/lmsStatus';
import { fetchApi, getHeaders } from './index';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function pickString(record: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
  }

  return '';
}

function pickNumber(record: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }

  return 0;
}

function pickBoolean(record: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  for (const key of keys) {
    if (!(key in record)) {
      continue;
    }

    const value = record[key];
    if (typeof value === 'boolean') {
      return value;
    }
  }

  return undefined;
}

function pickArray(record: Record<string, unknown>, ...keys: string[]): unknown[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
}

export function normalizeEnrollmentStatus(status: unknown): LmsEnrollmentStatus {
  if (typeof status !== 'string' || status.trim() === '') {
    return LmsEnrollmentStatus.NotEnrolled;
  }

  const normalized = status.replace(/[\s_-]/g, '').toLowerCase();
  if (normalized === 'completed') {
    return LmsEnrollmentStatus.Completed;
  }

  if (normalized === 'inprogress' || normalized === 'enrolled') {
    return LmsEnrollmentStatus.InProgress;
  }

  return LmsEnrollmentStatus.NotEnrolled;
}

function pickNullableNumber(record: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    if (!(key in record)) {
      continue;
    }

    const value = record[key];
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }

  return null;
}

function pickNullableString(record: Record<string, unknown>, ...keys: string[]): string | null {
  const value = pickString(record, ...keys);
  return value === '' ? null : value;
}

function pickNullableBoolean(record: Record<string, unknown>, ...keys: string[]): boolean | null {
  for (const key of keys) {
    if (!(key in record)) {
      continue;
    }

    const value = record[key];
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'boolean') {
      return value;
    }
  }

  return null;
}

export function canRetryLmsAssignment(input: {
  Status: LmsQuizStatus;
  AllowRetries: boolean;
  MaxRetries: number | null;
  AttemptCount: number;
}): boolean {
  if (input.Status !== LmsQuizStatus.Graded || !input.AllowRetries) {
    return false;
  }

  if (input.MaxRetries == null || input.MaxRetries <= 0) {
    return true;
  }

  return input.AttemptCount < input.MaxRetries;
}

export function normalizeQuizStatus(status: unknown, grade: number | null): LmsQuizStatus {
  if (grade !== null) {
    return LmsQuizStatus.Graded;
  }

  if (typeof status !== 'string' || status.trim() === '') {
    return LmsQuizStatus.NotStarted;
  }

  const normalized = status.replace(/[\s_-]/g, '').toLowerCase();
  if (normalized === 'graded') {
    return LmsQuizStatus.Graded;
  }

  if (normalized === 'submitted' || normalized === 'late') {
    return LmsQuizStatus.Submitted;
  }

  if (normalized === 'inprogress' || normalized === 'pending') {
    return LmsQuizStatus.InProgress;
  }

  return LmsQuizStatus.NotStarted;
}

function asQuestionList(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }

  const record = asRecord(value);
  if (!record) {
    return [];
  }

  return pickArray(record, 'questions', 'Questions');
}

function questionLevelCorrectIds(question: Record<string, unknown>): string[] {
  const single = pickString(question, 'correctAnswerId', 'correct_answer_id', 'correctAnswer', 'correct_answer', 'right_answer');
  if (single) {
    return [single];
  }

  return pickArray(question, 'correctAnswerIds', 'correct_answer_ids', 'correctAnswers', 'correct_answers').filter(
    (value): value is string => typeof value === 'string' && value.trim() !== '',
  );
}

function optionCorrectFlags(options: Record<string, unknown>[]): Array<boolean | null> {
  const explicit = options.map((option) => pickBoolean(option, 'correct', 'Correct', 'is_correct', 'isCorrect'));
  if (explicit.some((value) => value === true)) {
    return explicit.map((value) => value === true);
  }

  const assigned = options.map((option) => pickBoolean(option, 'assigned_right_answer', 'assignedRightAnswer'));
  const assignedCount = assigned.filter((value) => value === true).length;
  if (assignedCount > 0 && assignedCount < options.length) {
    return assigned.map((value) => value === true);
  }

  return explicit.map((value) => (value === undefined ? null : value));
}

export function normalizeQuizQuestions(raw: unknown): LmsQuizQuestion[] {
  return asQuestionList(raw).map((item, questionIndex) => {
    const question = asRecord(item) ?? {};
    const questionId = pickString(question, 'questionUUID', 'questionUuid', 'questionId', 'question_id', 'QuestionId', 'id') || `question-${questionIndex}`;
    const options = pickArray(question, 'answers', 'Answers', 'options', 'Options').map((option) => asRecord(option) ?? {});
    const correctIds = new Set(questionLevelCorrectIds(question));
    const optionFlags = optionCorrectFlags(options);

    return {
      questionId,
      question: pickString(question, 'question', 'Question', 'questionText', 'text'),
      type: pickString(question, 'type', 'Type') || 'multiple_choice',
      answers: options.map((answer, answerIndex) => {
        const answerId = pickString(answer, 'optionUUID', 'optionUuid', 'answerId', 'answer_id', 'AnswerId', 'id') || `${questionId}-answer-${answerIndex}`;

        return {
          answerId,
          text: pickString(answer, 'answer', 'Answer', 'text', 'Text'),
          correct: correctIds.size > 0 ? correctIds.has(answerId) : optionFlags[answerIndex],
        };
      }),
    };
  });
}

export function quizQuestionResult(question: LmsQuizQuestion, selectedIds: Iterable<string>): 'correct' | 'incorrect' | 'unanswered' | 'unknown' {
  const selected = [...selectedIds];
  const correctIds = question.answers.filter((answer) => answer.correct === true).map((answer) => answer.answerId);
  if (correctIds.length === 0 || correctIds.length === question.answers.length) {
    return 'unknown';
  }

  if (selected.length === 0) {
    return 'unanswered';
  }

  if (correctIds.length !== selected.length) {
    return 'incorrect';
  }

  return correctIds.every((id) => selected.includes(id)) ? 'correct' : 'incorrect';
}

function effectiveQuizMaxGrade(maxGrade: number | null, gradingType = ''): number | null {
  if (maxGrade !== null && maxGrade > 0) {
    return maxGrade;
  }

  if (gradingType === LmsGradingType.PERCENTAGE || gradingType === LmsGradingType.PASS_FAIL) {
    return 100;
  }

  return null;
}

function quizQuestionResultFromGrade(grade: number | null, maxGrade: number | null, gradingType = ''): 'correct' | 'incorrect' | 'unknown' {
  if (grade === null) {
    return 'unknown';
  }

  if (grade <= 0) {
    return 'incorrect';
  }

  const max = effectiveQuizMaxGrade(maxGrade, gradingType);
  if (max === null) {
    return 'unknown';
  }

  if (grade >= max) {
    return 'correct';
  }

  return 'unknown';
}

export function resolveQuizQuestionResult(
  question: LmsQuizQuestion,
  selectedIds: Iterable<string>,
  grade: number | null = null,
  maxGrade: number | null = null,
  gradingType = '',
): 'correct' | 'incorrect' | 'unanswered' | 'unknown' {
  const fromKey = quizQuestionResult(question, selectedIds);
  const fromGrade = quizQuestionResultFromGrade(grade, maxGrade, gradingType);
  if (fromGrade === 'incorrect') {
    return [...selectedIds].length === 0 ? 'unanswered' : 'incorrect';
  }

  if (fromKey !== 'unknown') {
    return fromKey;
  }

  return fromGrade;
}

export function quizAnswerKeyIsTrustworthy(
  question: LmsQuizQuestion,
  selectedIds: Iterable<string>,
  grade: number | null = null,
  maxGrade: number | null = null,
  gradingType = '',
): boolean {
  if (quizQuestionResultFromGrade(grade, maxGrade, gradingType) !== 'incorrect') {
    return true;
  }

  if (question.answers.every((answer) => answer.correct === true)) {
    return false;
  }

  const selected = new Set([...selectedIds]);
  return question.answers.some((answer) => answer.correct === true && !selected.has(answer.answerId));
}

function addSubmissionAnswers(selected: Record<string, string[]>, questionId: string, answerIds: string[]) {
  if (!questionId || answerIds.length === 0) {
    return;
  }

  selected[questionId] = answerIds;
}

function answerIdsFromUnknown(value: unknown): string[] {
  if (typeof value === 'string' && value.trim() !== '') {
    return [value];
  }

  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string' && item.trim() !== '');
}

function collectMappedAnswers(selected: Record<string, string[]>, value: unknown) {
  const record = asRecord(value);
  if (!record) {
    return;
  }

  for (const [questionId, answers] of Object.entries(record)) {
    addSubmissionAnswers(selected, questionId, answerIdsFromUnknown(answers));
  }
}

function parseSubmissionRows(selected: Record<string, string[]>, rows: unknown[]) {
  for (const item of rows) {
    const answer = asRecord(item);
    if (!answer) {
      continue;
    }

    const chosen = pickBoolean(answer, 'answer', 'Answer', 'selected', 'Selected');
    if (chosen === false) {
      continue;
    }

    const questionId = pickString(answer, 'questionUUID', 'questionUuid', 'questionId', 'question_id', 'QuestionId');
    const answerIds = pickArray(answer, 'answerIds', 'answer_ids', 'AnswerIds', 'selectedAnswerIds', 'selected_answer_ids', 'selected').filter(
      (value): value is string => typeof value === 'string' && value.trim() !== '',
    );
    const single = pickString(answer, 'optionUUID', 'optionUuid', 'answerId', 'answer_id', 'AnswerId');
    addSubmissionAnswers(selected, questionId, answerIds.length > 0 ? answerIds : single ? [single] : []);
  }
}

function normalizeQuizTaskSubmission(raw: unknown): Record<string, string[]> {
  const selected: Record<string, string[]> = {};
  const record = asRecord(raw);
  const source = record ? (asRecord(record.task_submission ?? record.TaskSubmission ?? record.submission) ?? record) : null;
  const listedAnswers = Array.isArray(raw) ? raw : source ? pickArray(source, 'submissions', 'Submissions', 'answers', 'Answers') : [];

  if (listedAnswers.length === 0 && source) {
    const mappedAnswers = asRecord(source.answers ?? source.Answers ?? source.SelectedAnswerIdsByQuestion ?? source.selectedAnswerIdsByQuestion);
    if (mappedAnswers) {
      collectMappedAnswers(selected, mappedAnswers);
    }
  }

  parseSubmissionRows(selected, listedAnswers);

  return selected;
}

export function mapSubmissionToQuestions(questions: LmsQuizQuestion[], selected: Record<string, string[]>): Record<string, string[]> {
  const optionToQuestion = new Map<string, string>();
  for (const question of questions) {
    for (const answer of question.answers) {
      optionToQuestion.set(answer.answerId, question.questionId);
    }
  }

  const mapped: Record<string, string[]> = {};
  for (const [questionId, answerIds] of Object.entries(selected)) {
    const knownQuestion = questions.some((question) => question.questionId === questionId);
    if (knownQuestion) {
      mapped[questionId] = [...(mapped[questionId] ?? []), ...answerIds];
      continue;
    }

    for (const answerId of answerIds) {
      const owner = optionToQuestion.get(answerId);
      if (!owner) {
        continue;
      }

      mapped[owner] = [...(mapped[owner] ?? []), answerId];
    }
  }

  return mapped;
}

export async function buildQuizTaskSubmission(questions: LmsQuizQuestion[], selectedByQuestion: Record<string, string[]>): Promise<LmsAssignmentSubmitTask['TaskSubmission']> {
  return {
    submissions: questions.flatMap((question) => {
      const selected = new Set(selectedByQuestion[question.questionId] ?? []);
      return question.answers.map((answer) => ({
        questionUUID: question.questionId,
        optionUUID: answer.answerId,
        answer: selected.has(answer.answerId),
      }));
    }),
  };
}

function normalizeQuizAttempt(raw: unknown): LmsQuizAttempt | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  return {
    AttemptNumber: pickNumber(record, 'AttemptNumber', 'attemptNumber', 'attempt_number'),
    Grade: pickNullableNumber(record, 'Grade', 'grade'),
    MaxGrade: pickNullableNumber(record, 'MaxGrade', 'maxGrade', 'max_grade'),
    GradingType: parseLmsGradingType(pickString(record, 'GradingType', 'gradingType', 'grading_type') || LmsGradingType.PERCENTAGE),
    PassThresholdPercentage: pickNullableNumber(record, 'PassThresholdPercentage', 'passThresholdPercentage', 'pass_threshold_percentage'),
    Passed: pickBoolean(record, 'Passed', 'passed') ?? false,
    SubmittedAt: pickString(record, 'SubmittedAt', 'submittedAt', 'submitted_at'),
  };
}

function normalizeQuizAttempts(raw: unknown): LmsQuizAttempt[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map(normalizeQuizAttempt).filter((attempt): attempt is LmsQuizAttempt => attempt !== null);
}

export function normalizeQuizSummary(raw: unknown): LmsQuizSummary | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const assignmentUuid = pickString(record, 'AssignmentUuid', 'assignmentUuid', 'assignment_uuid');
  const activityUuid = pickString(record, 'ActivityUuid', 'activityUuid', 'activity_uuid');
  if (!assignmentUuid || !activityUuid) {
    return null;
  }

  const grade = pickNullableNumber(record, 'Grade', 'grade');

  return {
    AssignmentUuid: assignmentUuid,
    ActivityUuid: activityUuid,
    Title: pickString(record, 'Title', 'title', 'Name', 'name') || 'Quiz',
    Status: normalizeQuizStatus(record.Status ?? record.status ?? record.SubmissionStatus ?? record.submission_status, grade),
    Grade: grade,
    MaxGrade: pickNullableNumber(record, 'MaxGrade', 'maxGrade', 'max_grade'),
    GradingType: parseLmsGradingType(pickString(record, 'GradingType', 'gradingType', 'grading_type') || LmsGradingType.PERCENTAGE),
    PassThresholdPercentage: pickNullableNumber(record, 'PassThresholdPercentage', 'passThresholdPercentage', 'pass_threshold_percentage'),
    Passed: pickNullableBoolean(record, 'Passed', 'passed') ?? false,
    Attempts: normalizeQuizAttempts(record.Attempts ?? record.attempts),
  };
}

export function normalizeQuizzes(raw: unknown): LmsQuizSummary[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map(normalizeQuizSummary).filter((quiz): quiz is LmsQuizSummary => quiz !== null);
}

function normalizeAssignmentTask(raw: unknown): LmsAssignmentTask | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const taskUuid = pickString(record, 'TaskUuid', 'taskUuid', 'task_uuid', 'assignment_task_uuid');
  if (!taskUuid) {
    return null;
  }

  const contents = record.Contents ?? record.contents ?? null;
  const submission = record.Submission ?? record.submission ?? record.task_submission ?? record.TaskSubmission ?? null;
  const questions = normalizeQuizQuestions(contents);
  const fromPayload = asRecord(record.SelectedAnswerIdsByQuestion ?? record.selectedAnswerIdsByQuestion);
  const selected = fromPayload ? collectMappedAnswersToRecord(fromPayload) : normalizeQuizTaskSubmission(submission);

  return {
    TaskUuid: taskUuid,
    Title: pickString(record, 'Title', 'title') || 'Quiz',
    Description: pickNullableString(record, 'Description', 'description'),
    AssignmentType: pickString(record, 'AssignmentType', 'assignmentType', 'assignment_type') || LmsAssignmentType.QUIZ,
    MaxGradeValue: pickNumber(record, 'MaxGradeValue', 'maxGradeValue', 'max_grade_value') || 100,
    Contents: contents,
    Questions: questions,
    Submission: submission,
    SelectedAnswerIdsByQuestion: mapSubmissionToQuestions(questions, selected),
    TaskGrade: pickNullableNumber(record, 'TaskGrade', 'taskGrade', 'task_grade', 'grade'),
  };
}

function collectMappedAnswersToRecord(value: unknown): Record<string, string[]> {
  const selected: Record<string, string[]> = {};
  collectMappedAnswers(selected, value);
  return selected;
}

export function normalizeAssignment(raw: unknown): LmsAssignment {
  const record = asRecord(raw) ?? {};
  const grade = pickNullableNumber(record, 'Grade', 'grade');
  const allowRetries = pickBoolean(record, 'AllowRetries', 'allowRetries', 'allow_retries') ?? false;
  const maxRetries = pickNullableNumber(record, 'MaxRetries', 'maxRetries', 'max_retries');
  const attemptCount = pickNumber(record, 'AttemptCount', 'attemptCount', 'attempt_count');
  const status = normalizeQuizStatus(record.Status ?? record.status ?? record.SubmissionStatus ?? record.submission_status, grade);
  const canRetry =
    pickBoolean(record, 'CanRetry', 'canRetry', 'can_retry') ??
    canRetryLmsAssignment({
      Status: status,
      AllowRetries: allowRetries,
      MaxRetries: maxRetries,
      AttemptCount: attemptCount,
    });

  return {
    AssignmentUuid: pickString(record, 'AssignmentUuid', 'assignmentUuid', 'assignment_uuid'),
    ActivityUuid: pickString(record, 'ActivityUuid', 'activityUuid', 'activity_uuid'),
    Title: pickString(record, 'Title', 'title') || 'Quiz',
    Description: pickNullableString(record, 'Description', 'description'),
    GradingType: parseLmsGradingType(pickString(record, 'GradingType', 'gradingType', 'grading_type') || LmsGradingType.PERCENTAGE),
    ShowCorrectAnswers: pickBoolean(record, 'ShowCorrectAnswers', 'showCorrectAnswers', 'show_correct_answers') ?? false,
    AllowRetries: allowRetries,
    MaxRetries: maxRetries,
    AttemptCount: attemptCount,
    CanRetry: canRetry,
    PassThresholdPercentage: pickNullableNumber(record, 'PassThresholdPercentage', 'passThresholdPercentage', 'pass_threshold_percentage'),
    Status: status,
    Passed: pickNullableBoolean(record, 'Passed', 'passed'),
    Grade: grade,
    MaxGrade: pickNullableNumber(record, 'MaxGrade', 'maxGrade', 'max_grade'),
    OverallFeedback: pickNullableString(record, 'OverallFeedback', 'overallFeedback', 'overall_feedback'),
    Tasks: pickArray(record, 'Tasks', 'tasks')
      .map(normalizeAssignmentTask)
      .filter((task): task is LmsAssignmentTask => task !== null),
  };
}

export function formatLmsQuizScore(quiz: Pick<LmsQuizSummary, 'Grade' | 'MaxGrade' | 'GradingType' | 'Status'>): string {
  if (quiz.Status !== LmsQuizStatus.Graded || quiz.Grade === null) {
    if (quiz.Status === LmsQuizStatus.InProgress) {
      return 'In progress';
    }

    if (quiz.Status === LmsQuizStatus.Submitted) {
      return 'Submitted';
    }

    return 'Not started';
  }

  if (quiz.GradingType === LmsGradingType.PERCENTAGE) {
    return `${quiz.Grade}%`;
  }

  if (quiz.GradingType === LmsGradingType.PASS_FAIL) {
    const passing = quiz.MaxGrade === null ? quiz.Grade > 0 : quiz.Grade >= quiz.MaxGrade;
    return passing ? 'Pass' : 'Fail';
  }

  if (quiz.MaxGrade !== null) {
    return `${quiz.Grade} / ${quiz.MaxGrade}`;
  }

  return String(quiz.Grade);
}

export function quizListHeadline(quizzes: LmsQuizSummary[]): string {
  if (quizzes.length === 0) {
    return 'No quizzes';
  }

  const graded = quizzes.filter((quiz) => quiz.Status === LmsQuizStatus.Graded).length;
  const started = quizzes.filter((quiz) => quiz.Status !== LmsQuizStatus.NotStarted).length;
  if (started === 0) {
    return `0 of ${quizzes.length} started`;
  }

  return `${graded} of ${quizzes.length} graded`;
}

export function replaceQuizSummary(quizzes: LmsQuizSummary[], nextQuiz: LmsQuizSummary): LmsQuizSummary[] {
  const existingIndex = quizzes.findIndex((quiz) => quiz.AssignmentUuid === nextQuiz.AssignmentUuid);
  if (existingIndex < 0) {
    return [...quizzes, nextQuiz];
  }

  return quizzes.map((quiz, index) => (index === existingIndex ? nextQuiz : quiz));
}

function normalizeActivity(raw: unknown): LmsActivity | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const activityUuid = pickString(record, 'activity_uuid', 'ActivityUuid', 'activityUuid');
  if (!activityUuid) {
    return null;
  }

  return {
    activity_uuid: activityUuid,
    name: pickString(record, 'name', 'Name') || 'Untitled activity',
    activity_type: pickString(record, 'activity_type', 'ActivityType', 'activityType'),
    activity_sub_type: pickString(record, 'activity_sub_type', 'ActivitySubType', 'activitySubType'),
    published: pickBoolean(record, 'published', 'Published') ?? true,
    is_completed: pickBoolean(record, 'IsCompleted', 'isCompleted', 'is_completed') ?? false,
    content: record.content ?? record.Content,
  };
}

function normalizeChapter(raw: unknown): LmsChapter | null {
  const record = asRecord(raw);
  if (!record) {
    return null;
  }

  const chapterUuid = pickString(record, 'chapter_uuid', 'ChapterUuid', 'chapterUuid');
  if (!chapterUuid) {
    return null;
  }

  return {
    chapter_uuid: chapterUuid,
    name: pickString(record, 'name', 'Name') || 'Untitled chapter',
    description: pickString(record, 'description', 'Description') || null,
    activities: pickArray(record, 'activities', 'Activities')
      .map(normalizeActivity)
      .filter((activity): activity is LmsActivity => activity !== null),
  };
}

function normalizeProgress(record: Record<string, unknown>): LmsCourseProgress {
  const completedUuids = pickArray(record, 'CompletedActivityUuids', 'completedActivityUuids', 'completed_activity_uuids').filter(
    (value): value is string => typeof value === 'string' && value.trim() !== '',
  );

  return {
    EnrollmentStatus: normalizeEnrollmentStatus(record.EnrollmentStatus ?? record.enrollmentStatus),
    CompletedActivityCount: pickNumber(record, 'CompletedActivityCount', 'completedActivityCount', 'completed_activity_count') || completedUuids.length,
    TotalActivityCount: pickNumber(record, 'TotalActivityCount', 'totalActivityCount', 'total_activity_count'),
    CompletedActivityUuids: completedUuids,
  };
}

export function normalizeCourseDetail(raw: unknown): LmsCourseDetail {
  const record = asRecord(raw) ?? {};
  const chapters = pickArray(record, 'Chapters', 'chapters')
    .map(normalizeChapter)
    .filter((chapter): chapter is LmsChapter => chapter !== null);

  return {
    CourseUuid: pickString(record, 'CourseUuid', 'courseUuid', 'course_uuid'),
    Name: pickString(record, 'Name', 'name') || 'Course',
    Description: pickString(record, 'Description', 'description') || null,
    Published: pickBoolean(record, 'Published', 'published') ?? true,
    EnrollmentStatus: normalizeEnrollmentStatus(record.EnrollmentStatus ?? record.enrollmentStatus),
    CompletedActivityCount: pickNumber(record, 'CompletedActivityCount', 'completedActivityCount', 'completed_activity_count'),
    TotalActivityCount: pickNumber(record, 'TotalActivityCount', 'totalActivityCount', 'total_activity_count'),
    Chapters: chapters,
    Quizzes: normalizeQuizzes(record.Quizzes ?? record.quizzes),
  };
}

async function readJson(resp: Response, errorMessage: string) {
  if (!resp.ok) {
    throw new Error(errorMessage);
  }

  return resp.json();
}

async function readErrorDetail(resp: Response, fallback: string): Promise<string> {
  try {
    const data = await resp.json();
    const record = asRecord(data);
    const detail = record ? pickString(record, 'detail', 'Detail') : '';
    return detail || fallback;
  } catch {
    return fallback;
  }
}

function courseUrl(courseUuid: string, path: string, sessionId: string) {
  return `/lms/courses/${encodeURIComponent(courseUuid)}${path}?sessionId=${encodeURIComponent(sessionId)}`;
}

export async function enrollLmsCourse(courseUuid: string, sessionId: string): Promise<LmsCourseProgress> {
  const resp = await fetchApi(courseUrl(courseUuid, '/enroll', sessionId), {
    method: 'POST',
    headers: getHeaders(),
  });
  const data = await readJson(resp, 'Failed to enroll in this course');
  return normalizeProgress(asRecord(data) ?? {});
}

export async function getLmsCourse(courseUuid: string, sessionId: string): Promise<LmsCourseDetail> {
  const resp = await fetchApi(courseUrl(courseUuid, '', sessionId), {
    headers: getHeaders(),
  });
  if (resp.status === 403) {
    throw new Error('This course is not available for your organization');
  }

  const data = await readJson(resp, 'Failed to load this course');
  return normalizeCourseDetail(data);
}

export async function getLmsAssignment(courseUuid: string, activityUuid: string, sessionId: string): Promise<LmsAssignment> {
  const resp = await fetchApi(courseUrl(courseUuid, `/activities/${encodeURIComponent(activityUuid)}/assignment`, sessionId), {
    headers: getHeaders(),
  });
  if (resp.status === 403) {
    throw new Error('This course is not available for your organization');
  }
  if (resp.status === 409) {
    throw new Error('Enroll in this course before taking the quiz');
  }
  if (resp.status === 404) {
    throw new Error('This activity is not a scored quiz');
  }

  const data = await readJson(resp, 'Failed to load this quiz');
  return normalizeAssignment(data);
}

export async function submitLmsAssignment(courseUuid: string, assignmentUuid: string, tasks: LmsAssignmentSubmitTask[], sessionId: string): Promise<LmsAssignmentSubmitResult> {
  const resp = await fetchApi(courseUrl(courseUuid, `/assignments/${encodeURIComponent(assignmentUuid)}/submit`, sessionId), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ tasks }),
  });
  if (resp.status === 403) {
    throw new Error('This course is not available for your organization');
  }
  if (resp.status === 409) {
    throw new Error('Enroll in this course before taking the quiz');
  }

  const data = await readJson(resp, 'Failed to submit this quiz');
  const record = asRecord(data) ?? {};
  const quiz = normalizeQuizSummary(record.Quiz ?? record.quiz);
  if (!quiz) {
    throw new Error('Quiz submission did not return a score');
  }

  return {
    ...normalizeProgress(record),
    Quiz: quiz,
  };
}

export async function retryLmsAssignment(courseUuid: string, assignmentUuid: string, sessionId: string): Promise<LmsAssignment> {
  const resp = await fetchApi(courseUrl(courseUuid, `/assignments/${encodeURIComponent(assignmentUuid)}/retry`, sessionId), {
    method: 'POST',
    headers: getHeaders(),
  });
  if (resp.status === 403) {
    throw new Error(await readErrorDetail(resp, 'Retries are not available for this quiz'));
  }
  if (resp.status === 409) {
    throw new Error('Enroll in this course before retrying the quiz');
  }
  if (resp.status === 400) {
    throw new Error(await readErrorDetail(resp, 'This quiz cannot be retried yet'));
  }

  const data = await readJson(resp, 'Failed to retry this quiz');
  return normalizeAssignment(data);
}

export class LmsCourseContentUpdatedError extends Error {
  constructor() {
    super('The content has been updated');
    this.name = 'LmsCourseContentUpdatedError';
  }
}

export async function completeLmsActivity(courseUuid: string, activityUuid: string, sessionId: string): Promise<LmsCourseProgress> {
  const resp = await fetchApi(courseUrl(courseUuid, `/activities/${encodeURIComponent(activityUuid)}/complete`, sessionId), {
    method: 'POST',
    headers: getHeaders(),
  });
  if (resp.status === 409) {
    const data = await resp.json().catch(() => null);
    const record = asRecord(data);
    if (record && pickBoolean(record, 'ContentUpdated', 'contentUpdated') === true) {
      throw new LmsCourseContentUpdatedError();
    }

    const detail = record ? pickString(record, 'detail', 'Detail') : '';
    throw new Error(detail || 'Failed to mark this activity complete');
  }

  const data = await readJson(resp, 'Failed to mark this activity complete');
  return normalizeProgress(asRecord(data) ?? {});
}

export async function getLmsActivityPdf(courseUuid: string | undefined, activityUuid: string): Promise<Blob> {
  if (!courseUuid) {
    throw new Error('Course UUID is required to load the LMS activity PDF.');
  }
  const resp = await fetchApi(`/lms/courses/${encodeURIComponent(courseUuid)}/activities/${encodeURIComponent(activityUuid)}/pdf`, {
    headers: getHeaders(),
  });
  if (!resp.ok) {
    throw new Error('Failed to fetch course PDF');
  }

  return resp.blob();
}

function blockMediaQuery(blockUuid?: string, fileFormat?: string) {
  const params = new URLSearchParams();
  if (blockUuid) {
    params.set('blockUuid', blockUuid);
  }
  if (fileFormat) {
    params.set('fileFormat', fileFormat);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function getLmsBlockImage(courseUuid: string | undefined, activityUuid: string, request: { fileId: string; blockUuid?: string; fileFormat?: string }): Promise<Blob> {
  if (!courseUuid) {
    throw new Error('Course UUID is required to load the LMS block image.');
  }
  const resp = await fetchApi(
    `/lms/courses/${encodeURIComponent(courseUuid)}/activities/${encodeURIComponent(activityUuid)}/blocks/${encodeURIComponent(request.fileId)}/image${blockMediaQuery(request.blockUuid, request.fileFormat)}`,
    {
      headers: getHeaders(),
    },
  );
  if (!resp.ok) {
    throw new Error('Failed to fetch course image');
  }

  return resp.blob();
}

export async function getLmsBlockPdf(courseUuid: string | undefined, activityUuid: string, request: { fileId: string; blockUuid?: string; fileFormat?: string }): Promise<Blob> {
  if (!courseUuid) {
    throw new Error('Course UUID is required to load the LMS block PDF.');
  }
  const resp = await fetchApi(
    `/lms/courses/${encodeURIComponent(courseUuid)}/activities/${encodeURIComponent(activityUuid)}/blocks/${encodeURIComponent(request.fileId)}/pdf${blockMediaQuery(request.blockUuid, request.fileFormat)}`,
    {
      headers: getHeaders(),
    },
  );
  if (!resp.ok) {
    throw new Error('Failed to fetch course PDF');
  }

  return resp.blob();
}

export const pwwLmsMediaFetchers: LmsMediaFetchers = {
  getBlockImage: getLmsBlockImage,
  getBlockPdf: getLmsBlockPdf,
  getActivityPdf: getLmsActivityPdf,
};
